import { Tag } from './app.entity';

/**
 * Main PDF template structure
 */
export interface PdfTemplate {
  enabled: boolean;
  pages: PdfTemplatePage[];
  orientation: 'portrait' | 'landscape';
  pageSize: 'A4' | 'letter';
}

/**
 * PDF template page with header, body, and footer sections
 */
export interface PdfTemplatePage {
  uuid: string;
  order: number;
  name?: string; // Custom name for the page (optional, defaults to "Page X")
  header: PdfTemplateSection;
  body: PdfTemplateSection;
  footer: PdfTemplateSection;
}

/**
 * Section within a page (header, body, or footer)
 */
export interface PdfTemplateSection {
  height?: number; // Height in mm (optional for body which is flexible)
  elements: PdfTemplateElement[];
  backgroundColor?: string; // Background color for the section (hex format)
}

/**
 * Base element interface
 */
export interface PdfTemplateElement {
  uuid: string;
  type: PdfTemplateElementType;
  x: number; // Position X in mm (relative to section)
  y: number; // Position Y in mm (relative to section)
  width?: number; // Width in mm (optional)
  height?: number; // Height in mm (optional)
  zIndex?: number; // Z-index for layering
}

/**
 * Element types
 */
export enum PdfTemplateElementType {
  LABEL = 'label',
  IMAGE = 'image',
  TABLE_VALUE = 'table-value',
  LINE = 'line',
  SHAPE = 'shape',
}

/**
 * Label element with text and styling
 */
export interface PdfTemplateLabel extends PdfTemplateElement {
  type: PdfTemplateElementType.LABEL;
  text: string;
  fontSize: number; // Font size in points
  fontFamily: string;
  color: string;
  alignment: 'left' | 'center' | 'right' | 'justify';
  fontWeight: 'normal' | 'bold' | 'italic' | 'bold-italic';
  bindingTagUuid?: string; // Optional tag UUID for data binding
  bindingTagValue?: string; // Optional tag value for easier reference
  fieldType?: string; // Form field type (e.g., 'free-text', 'multiple-choose', 'table')
}

/**
 * Image element with source and dimensions
 */
export interface PdfTemplateImage extends PdfTemplateElement {
  type: PdfTemplateElementType.IMAGE;
  assetKey?: string; // Asset key for uploaded images
  imageUrl?: string; // URL for dynamic images from form data
  bindingTagUuid?: string; // Optional tag UUID for dynamic images
  bindingTagValue?: string; // Optional tag value for easier reference
  fieldType?: string; // Form field type (e.g., 'file-upload')
  preserveAspectRatio: boolean;
}

/**
 * Table value element - displays form table data
 * Similar to app-pdf-page-table-viewer component
 */
export interface PdfTemplateTableValue extends PdfTemplateElement {
  type: PdfTemplateElementType.TABLE_VALUE;
  bindingFieldId: string; // Form field ID that contains the table
  bindingTagUuid: string; // Tag UUID for data binding
  bindingTagValue?: string; // Tag value for easier reference
  tableFontSize?: string; // e.g., 'text-sm', 'text-xs'
  cellPadding?: string; // e.g., 'p-2', 'pl-1 pb-3'
  maxWidth?: string; // e.g., '100%'
  emptyPlaceholder?: string;
}

/**
 * Line element for visual separators and borders
 */
export interface PdfTemplateLine extends PdfTemplateElement {
  type: PdfTemplateElementType.LINE;
  color: string; // Line color (hex format)
  rotation: number; // Rotation angle in degrees (0 = horizontal, 90 = vertical, etc.)
}

/**
 * Shape element for background rectangles and squares
 */
export interface PdfTemplateShape extends PdfTemplateElement {
  type: PdfTemplateElementType.SHAPE;
  width: number; // Width in mm
  height: number; // Height in mm
  backgroundColor: string; // Background color (hex format)
  borderWidth?: number; // Optional border width in pixels
  borderColor?: string; // Optional border color (hex format)
  borderRadius?: number; // Optional corner radius in pixels
}

/**
 * Data binding reference
 */
export interface PdfTemplateDataBinding {
  tagUuid: string;
  tagValue: string;
  fieldId: string;
  fieldLabel: string;
  fieldType: string;
}
